import { GridApi } from 'ag-grid-community'
import { CustomCellRendererProps } from 'ag-grid-react'
import { NegotiationDetail, NegotiationSide } from 'api'
import { NegotiationActions } from 'api/NegotiationActions'
import React, { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { userPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { AgGridWrapper, GridDetailLayout, GridId, useCenterGridPosition } from 'view/components/datagrid'
import {
  NestedLegItemWithIndex,
  PreAssignmentName,
  preAssignmentNamesToNegotiationFieldMeta,
  preAssignmentSubGridOptions,
  useRowData,
  useStopEditingOnClickOutsideGrid
} from 'view/shared/preAssignments'
import { shouldStartEdit } from '../shared/utils'
import { incomingPreAssignmentsSubGridColumnDefs } from './IncomingPreAssignmentsSubGridModel'

export interface IncomingPreAssignmentsSubGridProps extends CustomCellRendererProps<NegotiationDetail> {}

export const IncomingPreAssignmentsSubGrid = ({ data: negotiationDetail, api: parentApi }: IncomingPreAssignmentsSubGridProps) => {
  const dispatch = useDispatch()
  const userPreference = useSelector(userPreferenceSelector)
  const apiRef = useRef<GridApi>()
  const [position] = useCenterGridPosition(parentApi)
  const negotiationId = negotiationDetail?.negotiation.negotiationId
  const rowData = useRowData(negotiationDetail?.negotiation)

  const gridElRef = useRef<HTMLDivElement>(null)
  useStopEditingOnClickOutsideGrid({ api: apiRef.current, gridElRef })

  const canEditRow = useCallback(() => {
    if (negotiationDetail) {
      return shouldStartEdit(negotiationDetail)
    }

    return false
  }, [negotiationDetail])

  const onGridReady = useCallback(
    (gridApi: GridApi) => {
      apiRef.current = gridApi
      // resize after grid is settled
      setTimeout(() => gridApi.autoSizeAllColumns(), 100)
    },

    []
  )

  const onCellValueUpdated = useCallback(
    (rowId: string, _preAssignedTrade: NestedLegItemWithIndex, field: PreAssignmentName, newValue: string) => {
      if (!negotiationId) return
      const negotiationField = preAssignmentNamesToNegotiationFieldMeta(field)

      if (negotiationField) {
        dispatch(
          NegotiationActions.updatePreAssignment({
            assignmentIndex: parseFloat(rowId),
            field: negotiationField,
            negotiationId,
            updatedValue: newValue,
            negotiationSide: NegotiationSide.RESPONDER
          })
        )
      }
    },
    [dispatch, negotiationId]
  )

  return (
    <GridDetailLayout gridStart={position} gridContainerRef={gridElRef}>
      <AgGridWrapper
        className="datagrid-borderless"
        context={{ userPreference, gridId: GridId.IncomingNegotiationAssignmentsSubGrid, negotiationDetail }}
        canEditRow={canEditRow}
        onGridReady={onGridReady}
        onCellValueUpdated={onCellValueUpdated}
        gridOptions={preAssignmentSubGridOptions}
        columnDefs={incomingPreAssignmentsSubGridColumnDefs}
        rowData={rowData}
      />
    </GridDetailLayout>
  )
}
